/**
 * moonshot-locker-badge.js
 *
 * Adds a small "Locker · N · $X" badge to the storefront header for
 * logged-in customers with held items. Click → /account (where the
 * "Your Locker" customer-account block lives).
 *
 * Reads contents from the inventory-proxy Worker via the App Proxy
 * (so the request inherits the customer's logged-in identity — no
 * extra auth needed).
 *
 * Behavior:
 *   - Only renders for logged-in customers (we detect via response —
 *     a 401 from the Worker means guest, badge stays hidden).
 *   - Returns null for empty Lockers (no badge if no items).
 *   - Caches the result for 60 seconds in sessionStorage so we don't
 *     hammer the Worker on every page nav.
 *   - Re-renders if a `moonshot:cart:updated` or similar event fires.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const WORKER_PATH = "/apps/moonshot/customer/locker/contents";
  const STYLE_ID = "moonshot-locker-badge-styles";
  const BADGE_ID = "moonshot-locker-badge";
  const CACHE_KEY = "moonshot_locker_badge_cache";
  const CACHE_TTL_MS = 60 * 1000;

  function readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ at: Date.now(), data }),
      );
    } catch (e) {
      /* ignore */
    }
  }

  async function fetchContents() {
    const cached = readCache();
    if (cached) return cached;
    try {
      const res = await fetch(WORKER_PATH, {
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = await res.json();
      writeCache(data);
      return data;
    } catch (e) {
      return null;
    }
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #${BADGE_ID} {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #1d1f2b;
        color: #ffffff;
        font-size: 0.82rem;
        font-weight: 600;
        padding: 6px 12px;
        border-radius: 999px;
        text-decoration: none;
        line-height: 1;
        margin-right: 12px;
        font-family: inherit;
      }
      #${BADGE_ID}:hover {
        background: #2a2d3d;
      }
      #${BADGE_ID} .moonshot-locker-badge__icon {
        font-size: 0.95rem;
      }
      #${BADGE_ID} .moonshot-locker-badge__value {
        opacity: 0.8;
        font-weight: 500;
      }
    `;
    document.head.appendChild(style);
  }

  function buildBadge(data) {
    const a = document.createElement("a");
    a.id = BADGE_ID;
    a.href = "/account";
    a.setAttribute("title", "View your Moonshot Locker");

    const icon = document.createElement("span");
    icon.className = "moonshot-locker-badge__icon";
    icon.textContent = "🔒";
    a.appendChild(icon);

    const label = document.createElement("span");
    label.textContent = "Locker · " + data.itemCount;
    a.appendChild(label);

    if (data.totalValue > 0) {
      const value = document.createElement("span");
      value.className = "moonshot-locker-badge__value";
      value.textContent = "$" + data.totalValue.toFixed(2);
      a.appendChild(value);
    }

    return a;
  }

  function findHeaderInsertionPoint() {
    // Best-effort: insert before the cart icon, account icon, or
    // generic header utility area. Falls back gracefully.
    const candidates = [
      'header [data-cart-icon]',
      'header .header__cart',
      'header a[href*="/cart"]',
      'header a[href*="/account"]',
      'header .header__icons',
      'header .header__utility',
    ];
    for (const sel of candidates) {
      const el = document.querySelector(sel);
      if (el && el.parentElement) return el;
    }
    return null;
  }

  function removeExisting() {
    // Use querySelectorAll — when cart-refresh races with the theme's
    // section-rendering API, multiple badge elements can end up in the
    // DOM. getElementById returns only the FIRST, leaving the rest
    // behind = the "3 badges" stack Jayson hit on 2026-05-21.
    document
      .querySelectorAll("#" + BADGE_ID + ", [data-moonshot-locker-badge]")
      .forEach((el) => el.remove());
  }

  let renderInFlight = false;
  async function renderBadge() {
    // Single-flight guard. If a render is already in flight (mid-fetch),
    // the second call is a no-op — when the in-flight one finishes it
    // re-checks state from fresh and inserts the canonical badge.
    if (renderInFlight) return;
    renderInFlight = true;
    try {
      removeExisting();
      const data = await fetchContents();
      if (!data || !data.itemCount || data.itemCount === 0) return;
      injectStyles();
      // Re-check that nothing snuck in during the await — if another
      // call to renderBadge already inserted one, we don't want a
      // second one alongside it.
      removeExisting();
      const anchor = findHeaderInsertionPoint();
      if (!anchor) return; // header didn't match our selectors; bail
      const badge = buildBadge(data);
      badge.setAttribute("data-moonshot-locker-badge", "1");
      anchor.parentElement.insertBefore(badge, anchor);
    } finally {
      renderInFlight = false;
    }
  }

  let initDone = false;
  function init() {
    // Idempotency: protect against the IIFE being evaluated twice (e.g.
    // if the bundle is included from two places, or a SPA-like nav
    // re-executes assets). Multiple init() registrations meant multiple
    // cart:refresh listeners, multiplying badges on each event.
    if (initDone) return;
    initDone = true;
    renderBadge();
    document.addEventListener("cart:refresh", renderBadge);
    document.documentElement.addEventListener("cart:refresh", renderBadge);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
